<template>
  <tr
    class="border-b border-grey-300 leading-tight transition-all transition-fast"
  >
    <th class="px-4 py-2 text-left">
      <span class="flex items-center mb-1">
        <span
          v-if="assessment.stream && !isSingleStream"
          class="border rounded px-2 py-px align-center text-xs inline-block leading-none mr-2"
          :style="getStreamColorsByKey(assessment.stream)"
        >
          {{ getStreamNameByKey(assessment.stream) }}
        </span>
        <span class="truncate w-64">
          {{ assessment.company.name }}
        </span>
      </span>
      <small class="block font-normal truncate w-64">{{
        assessment.ref
      }}</small>
    </th>

    <td class="text-center">
      <template v-if="fullyAssessedCount">
        <div
          class="p-4 text-2xl leading-none text-white"
          :class="gradingBgColorScore(totalPercentage)"
        >
          <b>{{ totalPercentage }}<small>%</small></b>
          <small class="text-xs block">{{ totalScore }} / {{ maxScore }}</small>
        </div>
      </template>
      <template v-else>
        &mdash;
      </template>
    </td>

    <td
      v-if="booster"
      class="text-center"
    >
      <template v-if="fullyAssessedCount">
        <div
          class="p-4 text-2xl leading-none text-white"
          :class="gradingBgColorScore(totalBoosterPercentage)"
        >
          <b>{{ totalBoosterPercentage }}<small>%</small></b>
          <small class="text-xs block">Booster</small>
        </div>
      </template>
      <template v-else>
        &mdash;
      </template>
    </td>

    <td v-if="showRecommended" class="px-4 py-2 text-center font-bold">
      {{ totalRecommended }} / {{ numberOfAssessors }}
    </td>

    <td class="px-4 py-2 text-center">
      <app-badge
        v-if="approved && statusConfirmed"
        type="positive"
        icon="check"
      >
        {{ acceptedLabel }}
      </app-badge>
      <app-badge
        v-else-if="rejected && statusConfirmed"
        type="negative"
        icon="important"
      >
        {{ rejectedLabel }}
      </app-badge>
      <div
        v-else
        class="w-full border-2 pl-4 pr-2 py-2 flex"
        :class="{
          'focus-within:border-primary-500': !approved && !rejected,
          'focus-within:border-positive-800 focus-within:bg-white bg-positive-50 text-positive-900 border-positive-600': approved,
          'focus-within:border-negative-800 focus-within:bg-white bg-negative-50 text-negative-900 border-negative-600': rejected
        }"
      >
        <select
          v-model="assessment.pivot.status"
          class="appearance-none focus:outline-none flex-1 items-center -mr-6 bg-transparent text-inherit"
          :disabled="updating || statusConfirmed"
        >
          <option :value="null">
            Undecided
          </option>
          <option value="approve">
            {{ acceptLabel }}
          </option>
          <option value="reject">
            {{ rejectLabel }}
          </option>
        </select>
        <svg
          class="h-5 w-5"
          :class="{
            'text-grey-600': !approved && !rejected,
            'text-positive-600': approved,
            'text-negative-600': rejected
          }"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fill-rule="evenodd"
            d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
            clip-rule="evenodd"
          />
        </svg>
      </div>
    </td>

    <td class="px-4 py-2 text-center">
      <span
        v-if="statusConfirmed"
        class="w-6 inline-flex items-center justify-center h-6 font-bold text-white bg-positive-500"
      >
        &check;
      </span>
      <button
        v-else-if="assessment.pivot.status && !statusConfirmed"
        :disabled="updating"
        class="text-xs text-left uppercase font-bold border px-2 py-1 transition-all transition-normal no-underline"
        :class="{
          'text-positive-700 border-positive-500 hover:border-positive-700 hover:bg-positive-100 hover:text-positive-800': approved,
          'text-negative-700 border-negative-500 hover:border-negative-700 hover:bg-negative-100 hover:text-negative-800': rejected
        }"
        @click="confirmStatus"
      >
        Confirm
      </button>
    </td>
    <td class="relative">
      <popper
        trigger="click"
        :options="{
          placement: 'bottom'
        }"
        @show="toggleFocusInput(true)"
        @hide="toggleFocusInput(false)"
      >
        <div
          class="bg-white shadow-lg p-4 z-100 relative w-screen max-w-md border-t-4 border-primary-500"
        >
          <span
            class="absolute leading-none -mb-px text-xs text-primary-500"
            style="left: 50%; bottom:100%; transform: translateX(-50%)"
          >
            ▲
          </span>
          <label
            :for="`comment_${assessment.id}`"
            class="text-xs font-bold block text-grey-700 mb-1"
          >
            Additional Comments for {{ assessment.ref }}
          </label>
          <textarea
            :id="`comment_${assessment.id}`"
            :ref="`comment`"
            :value="assessment.pivot.comment"
            :disabled="updating || hasBeenNotified"
            :name="`comment_${assessment.id}`"
            :placeholder="
              `Please enter any additional comments that you would like to append to ${assessment.ref}'s email.`
            "
            class="focus:outline-none w-full leading-normal text-grey-600 focus:text-black transition-all transition-normal h-32 resize-none p-0"
            @blur="e => (assessment.pivot.comment = e.target.value)"
          />
        </div>
        <button
          slot="reference"
          type="button"
          class="flex items-center focus:outline-none mx-auto"
        >
          <icon
            icon="chat"
            class="w-6 h-6"
            :class="{
              'icons-primary': assessment.pivot.comment,
              'icons-grey': !assessment.pivot.comment
            }"
          />
        </button>
      </popper>
    </td>
    <td class="px-4 py-2 text-center">
      <funding-assessments-recipients-modal
        :disabled="updating || notificationIsDisabled || hasBeenNotified"
        :owner="assessment.user"
        :recipients="recipients"
        @save="doRecipientUpdate"
      />
    </td>
    <td class="px-4 py-2 text-center">
      <button
        v-if="approved || rejected"
        :disabled="updating || notificationIsDisabled"
        class="text-xs text-left uppercase font-bold border px-2 py-1 transition-all transition-normal no-underline ml-1"
        :class="{
          'border-grey-300 text-grey-400 cursor-not-allowed':
            updating || notificationIsDisabled,
          'text-secondary-700 border-secondary-500 hover:border-secondary-700 hover:bg-secondary-100 hover:text-secondary-800':
            !updating && !notificationIsDisabled
        }"
        @click="showPreview = true"
      >
        Preview
      </button>
      <portal to="modals">
        <app-modal v-if="showPreview" @close="showPreview = false">
          <div v-if="loading" class="bg-neutral-200 text-neutral-900 px-6 py-1">
            Generating Preview...
          </div>
          <div v-show="!loading">
            <p class="text-white font-bold mb-4 text-center">
              Email Preview for {{ assessment.ref }}
            </p>
            <dl class="bg-grey-200">
              <div class="border-b border-grey-300 px-6 py-3 flex">
                <dt class="font-bold mr-6">To:</dt>
                <dd>"{{ assessment.user.name }}" {{ assessment.user.email }}</dd>
              </div>
              <div class="border-b border-grey-300 px-6 py-3 flex">
                <dt class="font-bold mr-6">Cc:</dt>
                <div class="flex flex-wrap max-w-md">
                  <dd v-for="v in recipientsArray" :key="v" class="whitespace-no-wrap mr-1">{{ v }};</dd>
                </div>
              </div>
            </dl>
            <iframe
              class="w-screen max-w-lg"
              style="height: 70vh"
              :src="
                route('funding.assessments.phase.preview-email', [
                  $page.funding_pot.id,
                  $page.marking_phase.id
                ]) +
                  '?id=' +
                  assessment.id
              "
              @load="loading = false"
            ></iframe>
            <div class="mt-6 text-center">
              <button
                v-if="statusConfirmed && !hasBeenNotified"
                :disabled="updating || notificationIsDisabled"
                class="text-left uppercase font-bold border px-6 py-2 transition-all transition-normal no-underline ml-1"
                :class="{
                  'border-grey-300 text-grey-400 cursor-not-allowed':
                    updating || notificationIsDisabled,
                  'text-primary-700 border-primary-500 hover:border-primary-700 hover:bg-primary-100 hover:text-primary-800':
                    !updating && !notificationIsDisabled
                }"
                @click="sendNotification"
              >
                {{ updating ? "Sending..." : "Notify" }}
              </button>
            </div>
          </div>
        </app-modal>
      </portal>
    </td>
    <td class="px-4 py-2 text-center">
      <span
        v-if="hasBeenNotified"
        class="w-6 inline-flex items-center justify-center h-6 font-bold text-white bg-positive-500"
      >
        &check;
      </span>
      <button
        v-else-if="statusConfirmed && !hasBeenNotified"
        :disabled="updating || notificationIsDisabled"
        class="text-xs text-left uppercase font-bold border px-2 py-1 transition-all transition-normal no-underline ml-1"
        :class="{
          'border-grey-300 text-grey-400 cursor-not-allowed':
            updating || notificationIsDisabled,
          'text-primary-700 border-primary-500 hover:border-primary-700 hover:bg-primary-100 hover:text-primary-800':
            !updating && !notificationIsDisabled
        }"
        @click="sendNotification"
      >
        {{ updating ? "Sending..." : "Notify" }}
      </button>
    </td>
  </tr>
</template>
<script>
import { sum } from "lodash";
import Popper from "vue-popperjs";
import { competitionStreamsMixin } from "../../mixins/competitionStreams";

export default {
  name: "ReviewTableEntry",
  components: { Popper },
  mixins: [competitionStreamsMixin],
  props: {
    assessment: {
      type: Object,
      required: true
    },
    booster: {
      type: Object,
      required: false,
      default: null
    },
    showRecommended: {
      type: Boolean,
      required: false
    }
  },
  data() {
    return {
      showPreview: false,
      loading: true,
      updating: false
    };
  },
  computed: {
    acceptLabel() {
      return this.$page.marking_phase?.terminology?.accept ?? 'Approve'
    },
    rejectLabel() {
      return this.$page.marking_phase.terminology?.reject ?? 'Reject'
    },
    acceptedLabel() {
      return this.$page.marking_phase?.terminology?.accept ?? 'Approved'
    },
    rejectedLabel() {
      return this.$page.marking_phase.terminology?.reject ?? 'Rejected'
    },
    disabledNotifications() {
      return this.$page?.marking_phase?.template?.disabled_notifications ?? [];
    },

    notificationIsDisabled() {
      return this.disabledNotifications.includes(this.assessment.pivot.status);
    },

    approved() {
      return this.assessment.pivot.status === "approve";
    },

    rejected() {
      return this.assessment.pivot.status === "reject";
    },

    application() {
      return this.assessment.application || [];
    },

    numberOfAssessors() {
      return this.assessment.assessors.length;
    },

    fullyAssessedCount() {
      return this.assessment.all_assessor_grades_current_phase.length;
    },

    fullyAssessed() {
      return this.numberOfAssessors === this.fullyAssessedCount;
    },

    totalRecommended() {
      return this.assessment.all_assessor_grades_current_phase.filter(
        ag => ag.recommended
      ).length;
    },

    totalScore() {
      return sum(this.allAssessorTotals);
    },

    totalBoosterScore() {
      return sum(this.allAssessorBoosterScores);
    },

    totalPercentage() {
      return ((this.totalScore / this.maxScore) * 100).toFixed(2);
    },

    totalBoosterPercentage() {
      if(!this.maxBoosterScore) return 0;
      return parseFloat(((this.totalBoosterScore / this.maxBoosterScore) * 100).toFixed(2));
    },

    maxScore() {
      return (
        this.$page.marking_phase.template?.max_score * this.numberOfAssessors ??
        0
      );
    },

    maxBoosterScore() {
      if (!this.booster) return 0;
      return (
        this.booster?.max_score * this.numberOfAssessors ?? 0
      );
    },

    average() {
      return 0;
    },

    validateBoosterQuestionScores() {
      if (!this.booster) return false;
      return this.assessment.all_assessor_grades_current_phase.some(assessorGrade => {
        let scores = assessorGrade.scores.filter(score => this.booster.questions.includes(score.key)).map(score => score.grade ?? 0);
        return sum(scores) > 0;
      });
    },

    allAssessorBoosterScores() {
      if (!this.validateBoosterQuestionScores) return [];
      return this.assessment.all_assessor_grades_current_phase.reduce(
        (arr, assessorGrade) => {
          let scores = assessorGrade.scores.filter(score => this.booster.scores.includes(score.key)).map(score => score.grade ?? 0);
          arr.push(...scores);
          return arr;
        },
        []
      );
    },

    allAssessorAverages() {
      return this.assessment.all_assessor_grades_current_phase.reduce(
        (arr, assessorGrade) => {
          arr.push(assessorGrade.average);
          return arr;
        },
        []
      );
    },

    allAssessorTotals() {
      return this.assessment.all_assessor_grades_current_phase.reduce(
        (arr, assessorGrade) => {
          arr.push(assessorGrade.total);
          return arr;
        },
        []
      );
    },

    minRangeScore() {
      const min = this.min(this.allAssessorTotals);
      return isNaN(min) ? "-" : min;
    },
    maxRangeScore() {
      const max = this.max(this.allAssessorTotals);
      return isNaN(max) ? "-" : max;
    },

    rangeScore() {
      const range = this.maxRangeScore - this.minRangeScore;
      return isNaN(range) ? "-" : range;
    },

    statusConfirmed() {
      return (
        this.assessment.pivot.rejected_at || this.assessment.pivot.approved_at
      );
    },

    hasBeenNotified() {
      return Boolean(this.assessment.pivot.notified_at);
    },

    recipients() {
      const r = this.assessment.pivot.recipients ?? [];
      if (typeof r === "string") {
        try {
          const parsed = JSON.parse(r);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          return [];
        }
      }
      return r
    },

    recipientsArray() {
      return this.recipients.map(recipient => {
        if (recipient.name) {
          return `"${recipient.name}" <${recipient.email}>`;
        }
        return recipient.email;
      });
    },

    recipientsString() {
      return this.recipientsArray.join(", ");
    },

  },
  watch: {
    showPreview(val) {
      if (!val) this.loading = true;
    },
    "assessment.pivot": {
      handler(value) {
        this.updateAssessment(value);
      },
      deep: true
    }
  },
  methods: {
    gradingBgColorScore(score) {
      if (this.assessment.pivot.confirmed_at) {
        return {
          approve: "bg-positive-500",
          reject: "bg-negative-500"
        }[this.assessment.pivot.status];
      }

      if(!score) score = 0;

      return {
        0: "bg-grading-value-1",
        10: "bg-grading-value-2",
        20: "bg-grading-value-3",
        30: "bg-grading-value-4",
        40: "bg-grading-value-5",
        50: "bg-grading-value-6",
        60: "bg-grading-value-7",
        70: "bg-grading-value-8",
        80: "bg-grading-value-9",
        90: "bg-grading-value-10",
        100: "bg-grading-value-10"
      }[Math.floor(score / 10) * 10];
    },

    gradingTextColorScore(score) {
      if(!score) score = 0;

      return {
        0: "text-grading-value-1",
        10: "text-grading-value-2",
        20: "text-grading-value-3",
        30: "text-grading-value-4",
        40: "text-grading-value-5",
        50: "text-grading-value-6",
        60: "text-grading-value-7",
        70: "text-grading-value-8",
        80: "text-grading-value-9",
        90: "text-grading-value-10",
        100: "text-grading-value-10"
      }[Math.floor(score / 10) * 10];
    },

    gradingBorderColorScore(score) {
      if(!score) score = 0;
      return {
        0: "border-grading-value-1",
        10: "border-grading-value-2",
        20: "border-grading-value-3",
        30: "border-grading-value-4",
        40: "border-grading-value-5",
        50: "border-grading-value-6",
        60: "border-grading-value-7",
        70: "border-grading-value-8",
        80: "border-grading-value-9",
        90: "border-grading-value-10",
        100: "border-grading-value-10"
      }[Math.floor(score / 10) * 10];
    },

    updateAssessment({ confirm, notify }) {
      if (this.updating) return;
      this.updating = true;
      axios({
        method: "post",
        url: window.location.href,
        data: {
          id: this.assessment.id,
          status: this.assessment.pivot.status,
          comment: this.assessment.pivot.comment,
          recipients: this.assessment.pivot.recipients,
          confirm: confirm ?? null,
          notify: notify ?? null
        }
      })
        .then(({ data }) => (this.assessment.pivot = data))
        .catch(() =>
          alert("Unable to set status for this application, please try again.")
        )
        .finally(() => (this.updating = false));
    },
    confirmStatus() {
      this.updateAssessment({
        confirm: true
      });
    },
    sendNotification() {
      if (this.notificationIsDisabled) return;
      if (
        !confirm(
          `Are you sure you want to notify ${
            this.assessment.ref
          }? This will send them and email that will include the content sent in the ${
            this.approved ? this.acceptedLabel.toLowerCase() : this.rejectedLabel.toLowerCase()
          } email template, as well as any comments provided. The recipients are: "${this.assessment.user.name}" <${this.assessment.user.email}>, ${
            this.recipientsString
          }.`
        )
      )
        return;
      this.updateAssessment({
        notify: true
      });
    },
    toggleFocusInput(focus = false) {
      if (focus) {
        setTimeout(() => this.$refs.comment.focus(), 50);
      }
      this.$refs.comment.blur();
    },
    doRecipientUpdate(recipients) {
      this.assessment.pivot.recipients = recipients;
    },
  }
};
</script>
